import { Flex, HStack, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

import { Chip } from './Chip';

type SectionHeadingProps = {
  children: ReactNode;
  /** Счётчик элементов раздела — серая пилюля справа от подписи. */
  count?: number;
  /** `data-testid` для пилюли счётчика: E2E проверяют число, а не подпись целиком. */
  countTestId?: string;
  /** Уточнение обычным регистром: источник данных, окно времени. */
  hint?: ReactNode;
  /** Действие раздела — кнопка или ссылка, прижимается к правому краю. */
  action?: ReactNode;
};

/**
 * Подпись раздела — один приём на всё приложение.
 *
 * До этого разделы подписывались семью разными способами: капслок с разрядкой и волосяной линией
 * на профиле, заголовок в карточке настроек, голый `title` в Discover, он же с фиолетовой
 * пилюлей-счётчиком в календаре, а рядом — серой в комментариях. Один и тот же блок
 * «Предстоящие сессии» выглядел по-разному в календаре и в компакт-баре, то есть внутри одной
 * фичи. Каждое решение по отдельности защитимо, вместе они читаются как несколько сайтов.
 *
 * Кегль — `section` (md/600): `title` (sm/600) уже занят именами игр, датами и заголовками строк,
 * и подпись раздела на нём неотличима от содержимого под ней.
 *
 * Регистр обычный и без декора намеренно. Капслок с разрядкой оставлен только там, где он несёт
 * смысл, а не подписывает раздел: метки внутри игровых стат-карточек и разделители дней в чате.
 */
export const SectionHeading = ({
  children,
  count,
  countTestId,
  hint,
  action,
}: SectionHeadingProps) => (
  <HStack w="100%" gap={3} justifyContent="space-between" alignItems="center">
    <HStack gap={2} minW={0}>
      <Text as="h2" textStyle="section" truncate>
        {children}
      </Text>
      {count !== undefined ? (
        <Chip
          data-testid={countTestId}
          variant="fact"
          size="sm"
          flexShrink={0}
        >
          <Text textStyle="caption" color="fg.muted">
            {count}
          </Text>
        </Chip>
      ) : null}
      {hint ? (
        <Text textStyle="caption" color="fg.muted" truncate>
          {hint}
        </Text>
      ) : null}
    </HStack>
    {action ? <Flex flexShrink={0}>{action}</Flex> : null}
  </HStack>
);
